// ═══════════════════════════════════════════════════════════════════════════════
// Add / Edit family member (employer only)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isValidFamilyMemberBirthday, parseFamilyMemberBirthdayParts, type CreateFamilyMemberInput } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppButton } from '../../components/ui/AppButton';
import { useToast } from '../../components/ui/ToastProvider';
import { familyMemberApi } from '../../lib/api';
import {
  IconAlertTriangle,
  IconBirthdayCake,
  IconDocument,
  IconHash,
  IconHeartbeat,
  IconUtensils,
} from './familyMemberUi';

const pad2 = (n: number): string => String(n).padStart(2, '0');

export const FamilyMemberFormPage = () => {
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId: string }>();

  const isCreate = !memberId;

  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [remark, setRemark] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [foodPreference, setFoodPreference] = useState('');
  const [foodAllergy, setFoodAllergy] = useState('');
  const [medicalCondition, setMedicalCondition] = useState('');
  const [familyConsentConfirmed, setFamilyConsentConfirmed] = useState(false);

  const [loading, setLoading] = useState(!isCreate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(
    () => Array.from({ length: currentYear - 1899 }, (_, index) => String(currentYear - index)),
    [currentYear],
  );

  const maxDayInMonth = useMemo(() => {
    const m = parseInt(birthMonth, 10);
    if (!m) return 31;
    const y = birthYear ? parseInt(birthYear, 10) : 2000;
    return new Date(y, m, 0).getDate();
  }, [birthMonth, birthYear]);

  const dayOptions = useMemo(
    () => Array.from({ length: maxDayInMonth }, (_, index) => pad2(index + 1)),
    [maxDayInMonth],
  );

  useEffect(() => {
    if (!birthDay) return;
    const d = parseInt(birthDay, 10);
    if (d > maxDayInMonth) {
      setBirthDay(pad2(maxDayInMonth));
    }
  }, [maxDayInMonth, birthDay]);

  const loadMember = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await familyMemberApi.get(memberId);
      if (res.success && res.data) {
        const d = res.data;
        setName(d.name);
        setNickname(d.nickname ?? '');
        const parts = parseFamilyMemberBirthdayParts(d.birthday ?? '');
        setBirthYear(parts.year);
        setBirthMonth(parts.month);
        setBirthDay(parts.day);
        setRemark(d.remark ?? '');
        setHashtags(d.hashtags ?? '');
        setFoodPreference(d.foodPreference);
        setFoodAllergy(d.foodAllergy);
        setMedicalCondition(d.medicalCondition);
        setFamilyConsentConfirmed(false);
      } else {
        setError(res.error ?? t('family.loadError'));
      }
    } catch {
      setError(t('family.loadError'));
    } finally {
      setLoading(false);
    }
  }, [memberId, t]);

  useEffect(() => {
    if (isCreate) return;
    void loadMember();
  }, [isCreate, loadMember]);

  const buildBirthdayStorage = (): string | null => {
    const y = birthYear.trim();
    const m = birthMonth.trim();
    const d = birthDay.trim();
    if (!y && !m && !d) {
      return '';
    }
    if (!m || !d) {
      setError(t('family.validation.birthdayNeedMonthDay'));
      return null;
    }
    const mm = m.padStart(2, '0');
    const dd = d.padStart(2, '0');
    const built = y ? `${y}-${mm}-${dd}` : `--${mm}-${dd}`;
    if (!isValidFamilyMemberBirthday(built)) {
      setError(t('family.validation.birthdayInvalid'));
      return null;
    }
    return built;
  };

  const buildPayload = (): CreateFamilyMemberInput | null => {
    const birthdayValue = buildBirthdayStorage();
    if (birthdayValue === null) return null;
    if (!familyConsentConfirmed) {
      setError(t('family.validation.consentRequired'));
      return null;
    }
    return {
      name: name.trim(),
      nickname: nickname.trim(),
      birthday: birthdayValue,
      remark: remark.trim(),
      hashtags: hashtags.trim(),
      foodPreference: foodPreference.trim(),
      foodAllergy: foodAllergy.trim(),
      medicalCondition: medicalCondition.trim(),
      familyConsentConfirmed: true,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = buildPayload();
    if (!payload) return;

    setSubmitting(true);
    try {
      const res = isCreate
        ? await familyMemberApi.create(payload)
        : await familyMemberApi.update(memberId!, payload);

      if (res.success) {
        showSuccessToast(
          isCreate ? t('toast.familyMember.createSuccess') : t('toast.familyMember.updateSuccess'),
        );
        navigate('/family-members');
        return;
      }
      setError(res.error ?? t('family.saveError'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('family.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/family-members');
  };

  const handleConsentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setFamilyConsentConfirmed((c) => !c);
    }
  };

  const fieldShellClass =
    'rounded-xl border border-gray-200 bg-gray-50/90 p-4 dark:border-gray-700 dark:bg-gray-800/50';

  const labelRowClass = 'mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200';

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {isCreate ? t('family.formCreateTitle') : t('family.formEditTitle')}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 pb-24">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
          >
            <div>
              <label htmlFor="fm-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('family.field.name')}
              </label>
              <input
                id="fm-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
                autoComplete="name"
                placeholder={t('family.placeholder.name')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div>
              <label htmlFor="fm-nickname" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('family.formField.nickname')}
              </label>
              <input
                id="fm-nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={60}
                autoComplete="off"
                placeholder={t('family.placeholder.nickname')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconBirthdayCake />
                <span id="fm-birthday-legend">{t('family.formField.birthday')}</span>
              </div>
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">{t('family.birthday.optionalHint')}</p>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{t('family.birthday.exampleHint')}</p>
              <div
                className="grid grid-cols-3 gap-2"
                role="group"
                aria-labelledby="fm-birthday-legend"
              >
                <div>
                  <label htmlFor="fm-birth-year" className="sr-only">
                    {t('family.birthday.yearOptional')}
                  </label>
                  <select
                    id="fm-birth-year"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="">{t('family.birthday.yearOptional')}</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="fm-birth-month" className="sr-only">
                    {t('family.birthday.month')}
                  </label>
                  <select
                    id="fm-birth-month"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="">{t('family.birthday.month')}</option>
                    {Array.from({ length: 12 }, (_, index) => pad2(index + 1)).map((mo) => (
                      <option key={mo} value={mo}>
                        {mo}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="fm-birth-day" className="sr-only">
                    {t('family.birthday.day')}
                  </label>
                  <select
                    id="fm-birth-day"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="">{t('family.birthday.day')}</option>
                    {dayOptions.map((dy) => (
                      <option key={dy} value={dy}>
                        {dy}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconDocument />
                <label htmlFor="fm-remark">{t('family.formField.remark')}</label>
              </div>
              <textarea
                id="fm-remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder={t('family.field.remarkPlaceholder')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconUtensils />
                <label htmlFor="fm-pref">{t('family.formField.foodPreference')}</label>
              </div>
              <textarea
                id="fm-pref"
                value={foodPreference}
                onChange={(e) => setFoodPreference(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={t('family.placeholder.foodPreference')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconAlertTriangle />
                <label htmlFor="fm-allergy">{t('family.formField.foodAllergy')}</label>
              </div>
              <textarea
                id="fm-allergy"
                value={foodAllergy}
                onChange={(e) => setFoodAllergy(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={t('family.placeholder.foodAllergy')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconHeartbeat />
                <label htmlFor="fm-med">{t('family.formField.medicalCondition')}</label>
              </div>
              <textarea
                id="fm-med"
                value={medicalCondition}
                onChange={(e) => setMedicalCondition(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={t('family.placeholder.medicalCondition')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className={fieldShellClass}>
              <div className={labelRowClass}>
                <IconHash />
                <label htmlFor="fm-tags">{t('family.formField.hashtags')}</label>
              </div>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{t('family.field.hashtagsHint')}</p>
              <textarea
                id="fm-tags"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder={t('family.placeholder.hashtags')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-800 dark:bg-amber-950/60">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={familyConsentConfirmed}
                  onChange={(e) => setFamilyConsentConfirmed(e.target.checked)}
                  onKeyDown={handleConsentKeyDown}
                  tabIndex={0}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  aria-label={t('family.formConsentLabel')}
                />
                <span className="text-sm text-gray-800 dark:text-amber-100">{t('family.formConsentLabel')}</span>
              </label>
            </div>

            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-3 pt-2">
              <AppButton type="submit" variant="primary" disabled={submitting} className="flex-1 shadow-sm">
                {submitting ? t('family.formSubmitting') : t('family.formSubmit')}
              </AppButton>
              <AppButton type="button" variant="secondary" onClick={handleCancel}>
                {t('family.formCancel')}
              </AppButton>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};
